<template>
  <div class="bg-white border border-gray-200 rounded-lg">
    <div class="px-4 py-3 bg-amber-50 border-b border-amber-100 rounded-t-lg">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <h3 class="font-medium text-amber-900 text-sm">
            Alerts
            <span v-if="loaded && rows.length" class="text-gray-500 font-normal">({{ activeCount }} active)</span>
            <span v-else-if="totalActiveCount > 0 && !loaded" class="ml-1 inline-flex items-center px-1.5 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">{{ totalActiveCount }}</span>
          </h3>
          <select
            v-if="loaded"
            v-model="statusFilter"
            class="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
          >
            <option value="active">Active</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="resolved">Resolved</option>
            <option value="">All</option>
          </select>
        </div>
        <button
          v-if="!loaded"
          @click="fetchData"
          :disabled="loading"
          class="px-3 py-1.5 text-xs font-medium text-neutral-700 bg-neutral-50 border border-neutral-200 rounded hover:bg-neutral-100 transition-colors"
        >
          {{ loading ? 'Loading...' : 'View Alerts' }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-8">
      <LoadingIndicator />
    </div>

    <div v-else-if="loaded && filteredRows.length > 0" class="overflow-x-auto max-h-[400px] overflow-y-auto">
      <table class="w-full text-xs">
        <thead class="bg-gray-50 sticky top-0">
          <tr>
            <th class="px-3 py-2 font-medium text-gray-600 text-left whitespace-nowrap">Severity</th>
            <th v-if="!metricId" class="px-3 py-2 font-medium text-gray-600 text-left whitespace-nowrap cursor-pointer select-none hover:text-gray-900" @click="toggleSort('alert_name')">
              Alert
              <span v-if="sortKey === 'alert_name'" class="ml-0.5">{{ sortAsc ? '▲' : '▼' }}</span>
            </th>
            <th class="px-3 py-2 font-medium text-gray-600 text-left whitespace-nowrap cursor-pointer select-none hover:text-gray-900" @click="toggleSort('entity_name')">
              Entity
              <span v-if="sortKey === 'entity_name'" class="ml-0.5">{{ sortAsc ? '▲' : '▼' }}</span>
            </th>
            <th class="px-3 py-2 font-medium text-gray-600 text-right whitespace-nowrap cursor-pointer select-none hover:text-gray-900" @click="toggleSort('rate')">
              Rate
              <span v-if="sortKey === 'rate'" class="ml-0.5">{{ sortAsc ? '▲' : '▼' }}</span>
            </th>
            <th class="px-3 py-2 font-medium text-gray-600 text-right whitespace-nowrap">Num / Denom</th>
            <th class="px-3 py-2 font-medium text-gray-600 text-right whitespace-nowrap cursor-pointer select-none hover:text-gray-900" @click="toggleSort('percentile_rank')">
              Pctile
              <span v-if="sortKey === 'percentile_rank'" class="ml-0.5">{{ sortAsc ? '▲' : '▼' }}</span>
            </th>
            <th class="px-3 py-2 font-medium text-gray-600 text-right whitespace-nowrap">Attrib</th>
            <th class="px-3 py-2 font-medium text-gray-600 text-left whitespace-nowrap">Status</th>
            <th class="px-3 py-2 font-medium text-gray-600 text-left whitespace-nowrap">Assigned</th>
            <th class="px-3 py-2 font-medium text-gray-600 text-left whitespace-nowrap">Date</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in sortedRows"
            :key="row.id"
            class="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
            @click="selectedInstanceId = row.id"
          >
            <td class="px-3 py-2 whitespace-nowrap">
              <span :class="severityClass(rowSeverity(row))">{{ rowSeverity(row) }}</span>
            </td>
            <td v-if="!metricId" class="px-3 py-2 whitespace-nowrap text-gray-700">{{ row.alert_name }}</td>
            <td class="sensitive px-3 py-2 whitespace-nowrap font-medium text-gray-900">{{ row.entity_name }}</td>
            <td class="px-3 py-2 text-right whitespace-nowrap">{{ formatRate(row) }}</td>
            <td class="px-3 py-2 text-right whitespace-nowrap text-gray-500">{{ row.numerator }} / {{ row.denominator }}</td>
            <td class="px-3 py-2 text-right whitespace-nowrap">{{ row.percentile_rank != null ? row.percentile_rank.toFixed(0) + '%' : '—' }}</td>
            <td class="px-3 py-2 text-right whitespace-nowrap">{{ row.attribution }}</td>
            <td class="px-3 py-2 whitespace-nowrap">
              <select
                :value="row.status"
                @change="updateStatus(row, $event.target.value)"
                class="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white"
              >
                <option value="active">Active</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="resolved">Resolved</option>
              </select>
            </td>
            <td class="px-3 py-2 whitespace-nowrap text-gray-500">{{ row.assigned_to_name || '—' }}</td>
            <td class="px-3 py-2 whitespace-nowrap text-gray-500">{{ row.observation_date }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="loaded" class="flex items-center justify-center py-6 text-gray-400 text-sm">
      No alerts found
    </div>

    <!-- Alert Detail Slide-over -->
    <div
      v-if="selectedInstanceId"
      class="fixed inset-0 z-50 flex justify-end"
      @click.self="closeDetail"
    >
      <div class="absolute inset-0 bg-black/25" @click="closeDetail"></div>
      <div class="relative w-[720px] h-full bg-white shadow-xl overflow-y-auto">
        <div class="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
          <span class="font-medium text-sm text-gray-900">Alert Detail</span>
          <button @click="closeDetail" class="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
        </div>
        <div class="p-4">
          <ComposableAlertDetail
            :alert-instance-id="selectedInstanceId"
            @updated="fetchData"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import ComposableAlertDetail from '@/components/InsightsNew/ComposableAlertDetail.vue'

export default {
  name: 'ComposableAlertTable',

  components: { LoadingIndicator, ComposableAlertDetail },

  props: {
    metricIds: { type: Array, default: () => [] },
    year: { type: Number, default: null },
    entityId: { type: Number, default: null },
  },

  data() {
    return {
      loading: false,
      loaded: false,
      rows: [],
      statusFilter: 'active',
      sortKey: 'rate',
      sortAsc: true,
      selectedInstanceId: null,
      alertDefs: [],
    }
  },

  computed: {
    metricId() {
      return this.metricIds.length ? this.metricIds[0] : null;
    },
    totalActiveCount() {
      return this.alertDefs.reduce((sum, d) => sum + (d.instance_count || 0), 0)
    },
    activeCount() {
      return this.rows.filter(r => r.status === 'active').length
    },
    filteredRows() {
      if (!this.statusFilter) return this.rows
      return this.rows.filter(r => r.status === this.statusFilter)
    },
    sortedRows() {
      const rows = [...this.filteredRows]
      rows.sort((a, b) => {
        const av = a[this.sortKey] ?? ''
        const bv = b[this.sortKey] ?? ''
        return this.sortAsc ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1)
      })
      return rows
    },
  },

  // Props (metricIds especially) frequently arrive AFTER mount. These watchers
  // used to reset `loaded`/`rows` without re-fetching, so a first page visit
  // left the table blank until the user clicked Load or reloaded the page —
  // the reported "alerts don't load the first time" bug. Now: if the table had
  // already been loaded (or auto-load is on), re-fetch for the new props
  // instead of just clearing. Initial load stays lazy/on-demand by design.
  watch: {
    metricIds() { this.onCriteriaChanged(true) },
    year() { this.onCriteriaChanged() },
    entityId() { this.onCriteriaChanged() },
  },

  mounted() {
    this.fetchAlertDefs()
  },

  methods: {
    async fetchAlertDefs() {
      if (!this.metricId) return
      try {
        const response = await axios.get(`api/impact_analytics/composable-metric-alerts/?metric_id=${this.metricId}`)
        this.alertDefs = response.data.results || response.data || []
      } catch {
        this.alertDefs = []
      }
    },

    // Shared handler for metricIds/year/entityId changes. Re-fetches when the
    // table was already showing data so the view tracks the new criteria
    // rather than silently emptying.
    onCriteriaChanged(refreshDefs = false) {
      const wasLoaded = this.loaded
      this.loaded = false
      this.rows = []
      if (refreshDefs) this.fetchAlertDefs()
      if (wasLoaded) this.$nextTick(() => this.fetchData())
    },

    async fetchData() {
      if (!this.metricId && !this.entityId) return
      this.loading = true
      try {
        const params = new URLSearchParams()
        if (this.metricId) params.append('metric_id', this.metricId)
        if (this.year) params.append('year', this.year)
        if (this.entityId) params.append('entity_id', this.entityId)
        const response = await axios.get(`api/impact_analytics/composable-alert-instances/?${params}`)
        this.rows = response.data.results || response.data || []
        this.loaded = true
      } catch (error) {
        console.error('Error fetching alert instances:', error)
        this.rows = []
        this.loaded = true
      } finally {
        this.loading = false
      }
    },

    async updateStatus(row, newStatus) {
      const oldStatus = row.status
      row.status = newStatus
      try {
        await axios.patch(`api/impact_analytics/composable-alert-instances/${row.id}/`, {
          status: newStatus,
        })
      } catch (error) {
        console.error('Error updating status:', error)
        row.status = oldStatus
      }
    },

    formatRate(row) {
      const dc = row.display_config || {}
      const format = dc.format || 'percentage'
      if (format === 'count') return Math.round(row.numerator).toLocaleString()
      if (format === 'decimal') return row.rate.toFixed(dc.decimal_places ?? 1)
      return (row.rate * 100).toFixed(dc.decimal_places ?? 1) + '%'
    },

    severityClass(severity) {
      const base = 'inline-block px-1.5 py-0.5 text-xs font-medium rounded'
      if (severity === 'critical') return `${base} bg-red-100 text-red-800`
      if (severity === 'high') return `${base} bg-orange-100 text-orange-800`
      if (severity === 'medium') return `${base} bg-amber-100 text-amber-800`
      return `${base} bg-blue-100 text-blue-800`
    },

    rowSeverity(row) {
      return (row.detection_config || {}).severity || 'warning'
    },

    closeDetail() {
      this.selectedInstanceId = null
    },

    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortAsc = !this.sortAsc
      } else {
        this.sortKey = key
        this.sortAsc = true
      }
    },
  },
}
</script>
